/**
 * True Victory bundle builder — gates the bundle PDP form rendered by
 * snippets/tv-bundle-builder.liquid and serializes the shopper's component
 * selections into the properties[_components] hidden input, in slot order,
 * for the bundle-expand cart transform function (bundle-builder app repo).
 *
 * This is our own form, so gating is a plain submit intercept + disabled state —
 * none of the retired EB gate's workarounds (cart-attribute pump, MutationObserver,
 * button re-render defenses) are needed here. The validation rules and hint UX
 * match assets/text-property-gate.js, which gates the same two fields on normal
 * products, so both surfaces feel identical.
 *
 * Inert on any page without a .tv-bundle form.
 */
(function () {
  'use strict';

  const FORM_SELECTOR = 'product-form.tv-bundle form';
  const HINT_FLASH_MS = 2000;

  // Athlete fields (rendered per the metafield's personalization array). Same rules as
  // assets/text-property-gate.js: name 1-30 chars after trim; number 0-99 including "00".
  const FIELDS = [
    {
      selector: '.tv-bundle__athlete-name',
      emptyMsg: 'Enter the athlete name.',
      flashMsg: '30 characters max.',
      sanitize: function (value) {
        return value.slice(0, 30);
      },
      validate: function (trimmed) {
        return trimmed.length > 0 && trimmed.length <= 30;
      },
    },
    {
      selector: '.tv-bundle__athlete-number',
      emptyMsg: 'Enter a number from 0 to 99.',
      flashMsg: 'Numbers 0 to 99 only.',
      sanitize: function (value) {
        return value.replace(/\D/g, '').slice(0, 2);
      },
      validate: function (trimmed) {
        return /^\d{1,2}$/.test(trimmed);
      },
    },
  ];

  function getForm() {
    return document.querySelector(FORM_SELECTOR);
  }

  function slotSelects(form) {
    return Array.from(form.querySelectorAll('.tv-bundle__slot-select'));
  }

  function presentFields(form) {
    const present = [];
    FIELDS.forEach(function (desc) {
      const field = form.querySelector(desc.selector);
      if (field) present.push({ desc: desc, field: field });
    });
    return present;
  }

  function hintFor(field) {
    const id = field.getAttribute('aria-describedby');
    return id ? document.getElementById(id) : null;
  }

  function showHint(field, message) {
    const hint = hintFor(field);
    if (!hint) return;
    hint.textContent = message;
    hint.hidden = false;
  }

  function hideHint(field) {
    const hint = hintFor(field);
    if (hint && !hint.dataset.tvFlashing) hint.hidden = true;
  }

  function flashHint(field, message) {
    const hint = hintFor(field);
    if (!hint) return;
    hint.textContent = message;
    hint.hidden = false;
    hint.dataset.tvFlashing = '1';
    clearTimeout(flashHint._timer);
    flashHint._timer = setTimeout(function () {
      delete hint.dataset.tvFlashing;
      hint.hidden = true;
    }, HINT_FLASH_MS);
  }

  // The ordered payload the cart transform expects: one {id, q} per slot, or null while
  // any slot is unselected.
  function buildPayload(form) {
    const selections = [];
    const selects = slotSelects(form);
    for (const select of selects) {
      const id = Number(select.value);
      if (!select.value || !Number.isInteger(id)) return null;
      selections.push({ id: id, q: Number(select.dataset.slotQuantity) || 1 });
    }
    return selections.length ? selections : null;
  }

  function fieldsValid(form) {
    return presentFields(form).every(function (entry) {
      return entry.desc.validate(entry.field.value.trim());
    });
  }

  function firstInvalidControl(form) {
    const unselected = slotSelects(form).find(function (select) {
      return !select.value;
    });
    if (unselected) return unselected;
    const invalid = presentFields(form).find(function (entry) {
      return !entry.desc.validate(entry.field.value.trim());
    });
    return invalid ? invalid.field : null;
  }

  function sync(form) {
    const payload = buildPayload(form);
    const componentsInput = form.querySelector('.tv-bundle__components');
    if (componentsInput) componentsInput.value = payload ? JSON.stringify(payload) : '';

    const button = form.querySelector('.product-form__submit');
    const ready = !!payload && fieldsValid(form);
    if (button) {
      button.disabled = !ready;
      button.setAttribute('aria-disabled', String(!ready));
    }
    return ready;
  }

  // Slots seed with their first allowed variant's image; selecting a variant swaps the
  // slot image to that variant's own image (data-variant-image on the <option>), falling
  // back to the seeded image for variants without one.
  function updateSlotMedia(select) {
    const slot = select.closest('.tv-bundle__slot');
    const img = slot && slot.querySelector('.tv-bundle__slot-media img');
    if (!img) return;
    const option = select.selectedOptions[0];
    const src = (option && option.dataset.variantImage) || img.dataset.fallbackSrc;
    if (src) img.src = src;
  }

  function init() {
    const form = getForm();
    if (!form) return;

    form.addEventListener('change', function (event) {
      if (event.target.matches('.tv-bundle__slot-select')) {
        updateSlotMedia(event.target);
        sync(form);
      }
    });

    form.addEventListener('input', function (event) {
      const entry = presentFields(form).find(function (candidate) {
        return candidate.field === event.target;
      });
      if (!entry) return;
      const sanitized = entry.desc.sanitize(event.target.value);
      if (sanitized !== event.target.value) {
        event.target.value = sanitized;
        flashHint(event.target, entry.desc.flashMsg);
      } else if (entry.desc.validate(event.target.value.trim())) {
        hideHint(event.target);
      }
      sync(form);
    });

    // Document-level capture runs before Dawn's product-form.js submit handler regardless
    // of script order, so an invalid form never reaches the cart.
    document.addEventListener(
      'submit',
      function (event) {
        if (!form.contains(event.target)) return;
        if (sync(form)) return;
        event.preventDefault();
        event.stopPropagation();
        const control = firstInvalidControl(form);
        if (control) {
          control.focus();
          control.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const entry = presentFields(form).find(function (candidate) {
            return candidate.field === control;
          });
          if (entry) showHint(control, entry.desc.emptyMsg);
        }
      },
      true,
    );

    sync(form);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
